import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    journalTemplatesApi,
    journalEntriesApi,
    mealLogsApi,
    handleApiError,
} from '../services/journal-api';
import type {
    JournalType,
    CreateJournalEntryRequest,
    CreateMealLogRequest,
} from '../types/journal-api';
import { invalidateMealData } from '../lib/mealDataQueries';
import toast from 'react-hot-toast';

// Journal Templates Hooks
export const useJournalTemplates = (type?: JournalType, courseId?: string, isActive?: boolean) => {
    return useQuery({
        queryKey: ['journal-templates', type, courseId, isActive],
        queryFn: () => journalTemplatesApi.getAll(type, courseId, isActive),
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

export const useJournalTemplate = (id: string) => {
    return useQuery({
        queryKey: ['journal-template', id],
        queryFn: () => journalTemplatesApi.getById(id),
        enabled: !!id,
    });
};

export const useDefaultDailyTemplate = () => {
    return useQuery({
        queryKey: ['journal-template', 'default-daily'],
        queryFn: () => journalTemplatesApi.getDefaultDaily(),
        staleTime: 10 * 60 * 1000, // 10 minutes
    });
};

export const useCourseTemplates = (courseId: string) => {
    return useQuery({
        queryKey: ['journal-templates', 'course', courseId],
        queryFn: () => journalTemplatesApi.getForCourse(courseId),
        enabled: !!courseId,
        staleTime: 5 * 60 * 1000,
    });
};

// Journal Entries Hooks
export const useMyJournalEntries = (params?: {
    templateId?: string;
    courseId?: string;
    startDate?: string;
    endDate?: string;
    isCompleted?: boolean;
}) => {
    return useQuery({
        queryKey: ['my-journal-entries', params],
        queryFn: () => journalEntriesApi.getMyEntries(params),
    });
};

export const useMyJournalStats = (startDate?: string, endDate?: string) => {
    return useQuery({
        queryKey: ['my-journal-stats', startDate, endDate],
        queryFn: () => journalEntriesApi.getMyStats(startDate, endDate),
    });
};

export const useMyEntriesByDate = (date: string, templateId?: string) => {
    return useQuery({
        queryKey: ['my-journal-entries', 'by-date', date, templateId],
        queryFn: () => journalEntriesApi.getMyEntriesByDate(date, templateId),
        enabled: !!date,
    });
};

export const useJournalEntry = (id: string) => {
    return useQuery({
        queryKey: ['journal-entry', id],
        queryFn: () => journalEntriesApi.getById(id),
        enabled: !!id,
    });
};

export const useCreateJournalEntry = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: CreateJournalEntryRequest) => journalEntriesApi.create(data),
        onSuccess: (data) => {
            toast.success('Journal entry saved successfully!');
            // Invalidate relevant queries
            queryClient.invalidateQueries({ queryKey: ['my-journal-entries'] });
            queryClient.invalidateQueries({ queryKey: ['my-journal-stats'] });
            queryClient.invalidateQueries({
                queryKey: ['my-journal-entries', 'by-date', data.date.split('T')[0]],
            });
        },
        onError: (error) => {
            const message = handleApiError(error);
            toast.error(`Failed to save journal entry: ${message}`);
        },
    });
};

export const useUpdateJournalEntry = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: Partial<CreateJournalEntryRequest> }) =>
            journalEntriesApi.update(id, data),
        onSuccess: (data) => {
            toast.success('Journal entry updated successfully!');
            // Invalidate relevant queries
            queryClient.invalidateQueries({ queryKey: ['journal-entry', data.id] });
            queryClient.invalidateQueries({ queryKey: ['my-journal-entries'] });
            queryClient.invalidateQueries({ queryKey: ['my-journal-stats'] });
        },
        onError: (error) => {
            const message = handleApiError(error);
            toast.error(`Failed to update journal entry: ${message}`);
        },
    });
};

export const useDeleteJournalEntry = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => journalEntriesApi.delete(id),
        onSuccess: () => {
            toast.success('Journal entry deleted successfully!');
            // Invalidate relevant queries
            queryClient.invalidateQueries({ queryKey: ['my-journal-entries'] });
            queryClient.invalidateQueries({ queryKey: ['my-journal-stats'] });
        },
        onError: (error) => {
            const message = handleApiError(error);
            toast.error(`Failed to delete journal entry: ${message}`);
        },
    });
};

// Meal Logs Hooks
export const useMyMealLogs = (params?: {
    startDate?: string;
    endDate?: string;
    mealType?: string;
    mealSource?: string;
}) => {
    return useQuery({
        queryKey: ['my-meal-logs', params],
        queryFn: () => mealLogsApi.getMyMeals(params),
    });
};

export const useMyDailyMeals = (date: string) => {
    return useQuery({
        queryKey: ['my-daily-meals', date],
        queryFn: () => mealLogsApi.getMyDailyMeals(date),
        enabled: !!date,
    });
};

export const useMyDailyPointsSummary = (date: string) => {
    return useQuery({
        queryKey: ['my-daily-points-summary', date],
        queryFn: () => mealLogsApi.getMyDailyPointsSummary(date),
        enabled: !!date,
    });
};

export const useMyMealStats = () => {
    return useQuery({
        queryKey: ['my-meal-stats'],
        queryFn: () => mealLogsApi.getMyMealStats(),
    });
};

export const useMealLog = (id: string) => {
    return useQuery({
        queryKey: ['meal-log', id],
        queryFn: () => mealLogsApi.getById(id),
        enabled: !!id,
    });
};

export const useCreateMealLog = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: CreateMealLogRequest) => mealLogsApi.create(data),
        onSuccess: () => {
            toast.success('Meal logged successfully!');
            // Every view built on meal logs, by prefix: the daily queries for
            // every date (so no local-versus-UTC date bucketing can miss the
            // day the meal landed on), the streak, journal stats and the menus.
            return invalidateMealData(queryClient);
        },
        onError: (error) => {
            const message = handleApiError(error);
            toast.error(`Failed to log meal: ${message}`);
        },
    });
};

export const useDeleteMealLog = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => mealLogsApi.delete(id),
        onSuccess: () => {
            toast.success('Meal log deleted successfully!');
            // Deleting a meal re-scores the rest of its day and can break the
            // streak, so refresh everything built on meal logs.
            return invalidateMealData(queryClient);
        },
        onError: (error) => {
            const message = handleApiError(error);
            toast.error(`Failed to delete meal log: ${message}`);
        },
    });
};
